import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Cookie, Shield, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { isStandalone } from '../utils/pwa';

const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [details, setDetails] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    const sessionDismissed = sessionStorage.getItem('cookieConsentDismissed');
    // Authenticated users already accepted the privacy policy at signup -
    // don't re-prompt them with a banner that can overlay action buttons
    // on screens like the Bank Transactions Center.
    const isAuthenticated = !!localStorage.getItem('akountfly_user');

    // Feb 2026 · essential-only routes should not show the modal because it
    // covers action buttons on small screens (accept-invitation) and only
    // essential cookies run on those pages anyway.
    const path = (typeof window !== 'undefined' && window.location?.pathname) || '';
    const isEssentialOnlyRoute = (
      path.startsWith('/accept-invitation') ||
      path.startsWith('/verify-email') ||
      path.startsWith('/reset-password')
    );
    if (!consent && isEssentialOnlyRoute) {
      localStorage.setItem('cookieConsent', JSON.stringify({
        essential: true, analytics: false, marketing: false,
        timestamp: new Date().toISOString(), auto: 'essential-only-route',
      }));
      return;
    }

    // Installed app (PWA): no modal over the welcome experience. Only
    // essential cookies run, so no consent prompt is required - store an
    // essential-only choice silently (changeable later via Cookie Policy).
    if (!consent && isStandalone()) {
      localStorage.setItem('cookieConsent', JSON.stringify({
        essential: true, analytics: false, marketing: false,
        timestamp: new Date().toISOString(), auto: 'pwa-essential-only',
      }));
      return;
    }

    if (!consent && !sessionDismissed && !isAuthenticated) {
      setTimeout(() => setShow(true), 1000);
    }
  }, []);

  const logConsentToServer = async (consentData) => {
    try {
      await axios.post(`${API}/privacy/log-cookie-consent`, {
        essential: consentData.essential,
        analytics: consentData.analytics,
        marketing: consentData.marketing,
        policy_version: "1.0"
      }, { _retry: true });
    } catch (error) {
      try {
        await axios.post(`${API}/privacy/log-cookie-consent-anonymous`, {
          essential: consentData.essential,
          analytics: consentData.analytics,
          marketing: consentData.marketing,
          policy_version: "1.0"
        }, { _retry: true });
      } catch (e) {
        console.log('Cookie consent logged locally only');
      }
    }
  };

  const saveConsent = (consentData) => {
    const consent = {
      ...consentData,
      timestamp: new Date().toISOString(),
      policy_version: "1.0"
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    sessionStorage.setItem('cookieConsentDismissed', 'true');
    logConsentToServer(consentData);
    // Wire analytics-cookies choice to PostHog. When the user opts in,
    // capturing + session recording turn on; when they opt out (or
    // choose Essential only), all further events are dropped and we
    // clear any anonymous identifier the SDK may have staged before
    // consent. Without this the banner is decorative under GDPR.
    try {
      const ph = typeof window !== 'undefined' ? window.posthog : null;
      if (ph) {
        if (consentData.analytics) {
          ph.opt_in_capturing();
          try { ph.startSessionRecording && ph.startSessionRecording(); } catch { /* noop */ }
        } else {
          ph.opt_out_capturing();
          try { ph.stopSessionRecording && ph.stopSessionRecording(); } catch { /* noop */ }
          // Wipe anonymous distinct_id set before consent so we don't
          // accidentally correlate future events with pre-consent data.
          try { ph.reset(); } catch { /* noop */ }
        }
      }
    } catch { /* noop - never let PostHog break consent save */ }
    setShow(false);
  };

  const acceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true });
  };

  const acceptEssential = () => {
    saveConsent({ essential: true, analytics: false, marketing: false });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  if (!show) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] animate-in slide-in-from-bottom duration-300 max-h-[90dvh] overflow-y-auto pointer-events-none"
      data-testid="cookie-consent-modal"
    >
      <div className="max-w-4xl mx-auto bg-background border rounded-2xl shadow-2xl overflow-hidden pointer-events-auto">
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#108981]/10 flex items-center justify-center shrink-0">
              <Cookie className="w-6 h-6 text-[#108981]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                We value your privacy
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                By clicking &quot;Accept All&quot;, you consent to our use of cookies. 
                Read our <Link to="/privacy" className="text-[#108981] hover:underline">Privacy Policy</Link> and{' '}
                <Link to="/cookie-policy" className="text-[#108981] hover:underline">Cookie Policy</Link> for more information.
              </p>
              
              {details && <div className="space-y-3 mb-6" data-testid="cookie-detail-rows">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Shield className="w-4 h-4 text-[#108981]" />
                    <div>
                      <p className="font-medium text-sm">Essential Cookies</p>
                      <p className="text-xs text-muted-foreground">Required for authentication and security</p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground font-medium">Always Active</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300 text-[#108981] focus:ring-[#108981]"
                      data-testid="analytics-checkbox"
                    />
                    <div>
                      <p className="font-medium text-sm">Analytics Cookies</p>
                      <p className="text-xs text-muted-foreground">Help us understand how you use AkountFly</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300 text-[#108981] focus:ring-[#108981]"
                      data-testid="marketing-checkbox"
                    />
                    <div>
                      <p className="font-medium text-sm">Marketing Cookies</p>
                      <p className="text-xs text-muted-foreground">Used for relevant product updates</p>
                    </div>
                  </div>
                </div>
              </div>}

              <div className="flex flex-col sm:flex-row gap-3 items-center">
                {!details && (
                  <button type="button" onClick={() => setDetails(true)} className="text-sm font-semibold text-[#108981] hover:underline sm:mr-auto" data-testid="cookie-manage-btn">Manage cookies</button>
                )}
                <Button 
                  variant="outline" 
                  onClick={acceptEssential}
                  className="flex-1"
                  data-testid="essential-only-btn"
                >
                  Essential Only
                </Button>
                <Button 
                  variant="outline" 
                  onClick={savePreferences}
                  className="flex-1"
                  data-testid="save-preferences-btn"
                >
                  Save Preferences
                </Button>
                <Button 
                  onClick={acceptAll}
                  className="flex-1 bg-[#108981] hover:bg-[#108981]/90"
                  data-testid="accept-all-btn"
                >
                  Accept All
                </Button>
              </div>
            </div>
            <button
              onClick={acceptEssential}
              className="text-muted-foreground hover:text-foreground"
              data-testid="close-cookie-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
